import { useCallback, useMemo, useState } from 'react'
import {
  createPatientInputValidations,
  getAllValidationErrors,
  getValidationMessage,
  isFormValid,
  validateAttendants,
  validateAlternativeEmails,
  validateAlternativeMobiles,
} from '../utils/patientValidationUtils'

/**
 * Validation state for the patient form.
 *
 * Errors are only *shown* for fields the user has touched (blurred) or once the host asks to
 * reveal everything (`revealAllErrors`), so a freshly opened form does not shout "Name is
 * required" on every field. `isValid` / `hasErrors` always reflect the real state so the host
 * can gate its submit button.
 */
export const usePatientValidation = (
  patientDetails,
  makeAnonymousPatient,
  hideInputValidations = false,
  translateValidationMessage = (message) => message,
  { revealAllErrors = false } = {},
) => {
  const [touchedFields, setTouchedFields] = useState({})

  const markTouched = useCallback((fieldKey) => {
    if (!fieldKey) return
    setTouchedFields((prev) => (prev[fieldKey] ? prev : { ...prev, [fieldKey]: true }))
  }, [])

  const resetTouched = useCallback(() => setTouchedFields({}), [])

  const validations = useMemo(
    () => createPatientInputValidations(patientDetails, makeAnonymousPatient, hideInputValidations),
    [patientDetails, makeAnonymousPatient, hideInputValidations],
  )

  const validationErrors = useMemo(
    () => getAllValidationErrors(patientDetails, makeAnonymousPatient),
    [patientDetails, makeAnonymousPatient],
  )

  const attendantValidations = useMemo(
    () => validateAttendants(patientDetails?.attendants),
    [patientDetails?.attendants],
  )

  const alternativeMobileValidations = useMemo(
    () => validateAlternativeMobiles(patientDetails?.altMobiles),
    [patientDetails?.altMobiles],
  )

  const alternativeEmailValidations = useMemo(
    () => validateAlternativeEmails(patientDetails?.altEmails),
    [patientDetails?.altEmails],
  )

  const isValid = useMemo(
    () => isFormValid(patientDetails, makeAnonymousPatient),
    [patientDetails, makeAnonymousPatient],
  )

  const hasErrors =
    validationErrors.length > 0 ||
    attendantValidations.length > 0 ||
    alternativeMobileValidations.length > 0 ||
    alternativeEmailValidations.length > 0

  const canShow = useCallback(
    (fieldKey) => !hideInputValidations && (revealAllErrors || Boolean(touchedFields[fieldKey])),
    [hideInputValidations, revealAllErrors, touchedFields],
  )

  const getFieldValidation = useCallback(
    (fieldName) => {
      const fieldIsValid = validations[fieldName] !== false
      const errorMessage = fieldIsValid
        ? null
        : translateValidationMessage(getValidationMessage(fieldName, patientDetails))
      return {
        isValid: fieldIsValid,
        showError: !fieldIsValid && canShow(fieldName),
        errorMessage,
      }
    },
    [validations, translateValidationMessage, patientDetails, canShow],
  )

  const buildIndexedGetter = useCallback(
    (list, keyPrefix, { alwaysShow = false } = {}) =>
      (index) => {
        const validation = list.find((entry) => entry.index === index)
        return {
          isValid: !validation,
          showError:
            Boolean(validation) &&
            !hideInputValidations &&
            (alwaysShow || canShow(`${keyPrefix}.${index}`)),
          errorMessage: validation?.message ? translateValidationMessage(validation.message) : null,
        }
      },
    [canShow, hideInputValidations, translateValidationMessage],
  )

  // Attendant errors only exist for "contact without a name" rows, which would be dropped on
  // save, so they are shown immediately rather than waiting for a blur.
  const getAttendantValidation = useMemo(
    () => buildIndexedGetter(attendantValidations, 'attendant', { alwaysShow: true }),
    [buildIndexedGetter, attendantValidations],
  )
  const getAlternativeMobileValidation = useMemo(
    () => buildIndexedGetter(alternativeMobileValidations, 'altMobile'),
    [buildIndexedGetter, alternativeMobileValidations],
  )
  const getAlternativeEmailValidation = useMemo(
    () => buildIndexedGetter(alternativeEmailValidations, 'altEmail'),
    [buildIndexedGetter, alternativeEmailValidations],
  )

  const getAllErrors = () => ({
    fieldErrors: validationErrors,
    attendantErrors: attendantValidations,
    alternativeMobileErrors: alternativeMobileValidations,
    alternativeEmailErrors: alternativeEmailValidations,
  })

  const getErrorSummary = () => {
    const allErrors = getAllErrors()
    const totalErrors =
      allErrors.fieldErrors.length +
      allErrors.attendantErrors.length +
      allErrors.alternativeMobileErrors.length +
      allErrors.alternativeEmailErrors.length

    return {
      totalErrors,
      hasFieldErrors: allErrors.fieldErrors.length > 0,
      hasAttendantErrors: allErrors.attendantErrors.length > 0,
      hasAlternativeMobileErrors: allErrors.alternativeMobileErrors.length > 0,
      hasAlternativeEmailErrors: allErrors.alternativeEmailErrors.length > 0,
      summary: `${totalErrors} validation error${totalErrors !== 1 ? 's' : ''} found`,
    }
  }

  return {
    validations,
    validationErrors,
    attendantValidations,
    alternativeMobileValidations,
    alternativeEmailValidations,
    isValid,
    hasErrors,
    touchedFields,
    markTouched,
    resetTouched,
    getFieldValidation,
    getAttendantValidation,
    getAlternativeMobileValidation,
    getAlternativeEmailValidation,
    getAllErrors,
    getErrorSummary,
  }
}

export default usePatientValidation
